import { getCompanyConfig } from 'lib/api/company';
import { getAllPaymentRequestPassives } from 'lib/api/paymentRequestPassives';
import { getCompanyPayoutGroup } from 'lib/api/payments';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import CustomAlert from 'lib/designSystem/Alert';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from 'lib/designSystem/HeaderWithActions';
import TableWithPagination from 'lib/designSystem/TableWithPagination';
import { hasAnyPermissions } from 'lib/functions/utils';
import { PaymentRequestPassive } from 'lib/types/models';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AuthState } from 'services/redux/authSlice';

import NewPaymentRequestPassive from './components/Create';
import PayOutDetails from './components/Details';
import ReverseConfirmation from './components/ReverseConfirmation';

const headers: { key: string; label: string; type?: any }[] = [
  { key: 'id', label: 'Id', type: 'numeric-id' },
  { key: 'businessName', label: 'Cliente' },
  { key: 'businessIdentifier', label: 'Rut cliente' },
  { key: 'amountApplied', label: 'Aplicado', type: 'money' },
  { key: 'amountToDisccount', label: 'A descontar', type: 'money' },
  { key: 'amount', label: 'Monto Giro', type: 'money' },
  { key: 'amountInNominee', label: 'Monto en nómina', type: 'money' },
  { key: 'createdAt', label: 'Fecha de creación', type: 'date' },
  { key: 'status', label: 'Estado', type: 'status' },
  // { key: 'executedAt', label: 'Completada', type: 'date' },
];

const PaymentRequestPassives: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [paymentRequestPassives, setPaymentRequestPassives] = useState<
    PaymentRequestPassive[]
  >([]);
  const [paymentRequestPassiveDetails, setPaymentRequestPassiveDetails] =
    useState<PaymentRequestPassive | null>(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState('');
  const [failure, setFailure] = useState('');
  const [config, setConfig] = useState<string | null>(null);

  const [openDetails, setOpenDetails] = useState(false);
  const [openReverse, setOpenReverse] = useState(false);
  const [selectedPaymentRequestId, setSelectedPaymentRequestId] = useState('');

  const [endDate, setEndDate] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [searchId, setSearchId] = useState('');
  const [searchStatuses, setSearchStatuses] = useState<
    Record<string, string>[]
  >([
    { label: 'En giro', value: 'IN_TRANSFER' },
    { label: 'En revisión', value: 'IN_REVIEW' },
  ]);
  const [tableLoading, setTableLoading] = useState(false);
  const PAGE_SIZE = 10;
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    totalPages: 0,
  });

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (searchId) query += `id=${searchId}&`;
    if (startDate) query += `startDate=${startDate}&`;
    if (endDate) query += `endDate=${endDate}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (businessName)
      query += `businessName=${businessName.replace('&', '%26')}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}&`;
    if (searchStatuses.length > 0) {
      let stringForQuery = '';
      for (const status of searchStatuses) {
        stringForQuery += `${status.value},`;
      }
      stringForQuery = stringForQuery.slice(0, -1);
      query += `statuses=${stringForQuery}&`;
    }
    console.log('QUERY:', query);
    return query;
  };

  async function setUpConfig() {
    const response = await getCompanyConfig(
      dispatch,
      'system_operations_payment_request_passive_mandatory_approval',
    );
    setConfig(response);
  }

  const fetchPaymentRequestPassives = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    try {
      const query = generateQuery({ page, pageSize });
      const response = await getAllPaymentRequestPassives(dispatch, query);
      setPaymentRequestPassives(response.data);
      setPagination(response.pagination);
      return response;
    } catch (error) {
      alert('Error al cargar los pagos');
    }
  };

  const triggerSearch = async () => {
    setLoading(true);
    await fetchPaymentRequestPassives({ page: 0, pageSize: PAGE_SIZE });
    setLoading(false);
  };

  const setUpData = async () => {
    setLoading(true);
    await fetchPaymentRequestPassives({ page: 0, pageSize: PAGE_SIZE });
    setLoading(false);
  };

  const reloadDetails = async () => {
    if (!paymentRequestPassiveDetails) return;
    const id = paymentRequestPassiveDetails.id;
    try {
      const query = generateQuery({ page: 0, pageSize: PAGE_SIZE });
      const response = await getAllPaymentRequestPassives(dispatch, query);
      setPaymentRequestPassives(response.data);
      setPagination(response.pagination);
      const payment: any = paymentRequestPassives.find(
        (payment: any) => payment.id === id,
      );
      if (payment) {
        const paymentGroupedDetailResponse = await getCompanyPayoutGroup(
          dispatch,
          'payment-request-passive',
          payment.id,
        );
        setPaymentRequestPassiveDetails(paymentGroupedDetailResponse);
        setOpenDetails(true);
      }
      return response;
    } catch (error) {
      alert('Error al cargar los pagos');
    }
  };

  useEffect(() => {
    void setUpData();
    void setUpConfig();
  }, []);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleDetails = async (id: string) => {
    setLoading(true);
    const payment: any = paymentRequestPassives.find(
      (payment: any) => payment.id === id,
    );
    if (payment) {
      try {
        const paymentGroupedDetailResponse = await getCompanyPayoutGroup(
          dispatch,
          'payment-request-passive',
          payment.id,
        );
        setPaymentRequestPassiveDetails(paymentGroupedDetailResponse);
        setOpenDetails(true);
      } catch (error) {
        console.error('error', error);
        alert('Error al cargar los pagos');
      }
      setLoading(false);
    }
    setLoading(false);
  };

  const handleReverse = (id: string) => {
    setSelectedPaymentRequestId(id);
    setOpenReverse(true);
  };

  const actions = [
    {
      action: handleDetails,
      actionLabel: 'Detalles',
    },
    {
      action: handleReverse,
      actionLabel: 'Reversar',
      canShow: () => {
        return hasAnyPermissions(user, [
          'financing_payment_request_passive_reverse',
        ]);
      },
    },
  ];

  const filters: Filter[] = [
    {
      label: 'ID',
      type: 'text',
      value: searchId,
      setter: setSearchId,
    },
    {
      label: 'Estado',
      type: 'multiple',
      value: searchStatuses,
      setter: setSearchStatuses,
      options: [
        { label: 'Completada', value: 'COMPLETED' },
        { label: 'En giro', value: 'IN_TRANSFER' },
        { label: 'En revisión', value: 'IN_REVIEW' },
        { label: 'Rechazada', value: 'REJECTED' },
      ],
    },
    {
      label: 'Fecha de contabilización',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha de contabilización',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
    {
      label: 'Nombre cliente',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
    {
      label: 'Rut cliente',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
  ];

  const buttons: CustomButtonProps[] = [
    {
      name: 'Buscar',
      action: () => {},
      color: 'primary',
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={triggerSearch}
          dispatch={dispatch}
          disableSearch={loading}
        />
      ),
    },
    {
      name: 'Crear',
      action: handleOpen,
      color: 'primary',
      disabled: !hasAnyPermissions(user, [
        'financing_payout_new',
        'financing_payment_request_passive_create',
      ]),
    },
  ];

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        title="Acá encontrarás todas las devoluciones de pasivos y sus aplicaciones."
        variant="body1"
        buttons={buttons}
      />
      <TableWithPagination
        dataHeaders={headers}
        data={paymentRequestPassives}
        actions={actions}
        showActions
        lastElement
        setData={setPaymentRequestPassives}
        pagination={pagination}
        loading={tableLoading}
        setLoading={setTableLoading}
        setPagination={setPagination}
        searchData={fetchPaymentRequestPassives}
      />
      {open && (
        <NewPaymentRequestPassive
          setOpen={setOpen}
          open={open}
          setSuccessOpen={setSuccess}
          setFailureOpen={setFailure}
          setUp={triggerSearch}
          config={config}
        />
      )}
      {openDetails && (
        <PayOutDetails
          open={openDetails}
          setOpen={setOpenDetails}
          paymentGroupedDetails={paymentRequestPassiveDetails}
          setSuccess={setSuccess}
          setFailure={setFailure}
          setUp={reloadDetails}
          config={config}
        />
      )}
      {openReverse && (
        <ReverseConfirmation
          open={openReverse}
          setOpen={setOpenReverse}
          paymentRequestId={selectedPaymentRequestId}
          setSuccess={setSuccess}
          setFailure={setFailure}
          setUp={triggerSearch}
        />
      )}
      <CustomAlert
        open={success !== ''}
        title="Éxito"
        onClose={() => {
          setSuccess('');
        }}
        text={success}
        severity="success"
        size="small"
        variant="outlined"
      />
      <CustomAlert
        open={failure !== ''}
        title="Error"
        onClose={() => {
          setFailure('');
        }}
        text={failure}
        severity="error"
        size="small"
        variant="outlined"
      />
    </div>
  );
};

export default PaymentRequestPassives;
